import styled, {css} from 'styled-components';
import breakpoint from '../../../theme/constants/breakpoints';
import colors from '../../../theme/constants/colors';
import Typography from '../../../theme/typography';
import {
  StyledFilterDropdownButtonProps,
  StyledBodyProps,
  StyledFilterDropdownProps,
} from './typings';
import zIndex from '../../../theme/constants/zIndex';
import scrollbar from '@/theme/elements/scrollbar';

export const StyledFilterDropdownContainer = styled.div`
  position: relative;
  width: fit-content;
`;

const StyledFilterDropdown = styled.section<StyledFilterDropdownProps>`
  top: 0;
  ${(props) => `${props.$position}: 0;`}
  width: 100vw;
  height: 100vh;
  position: fixed;
  margin: 0;
  z-index: ${zIndex.modal};
  background: ${colors.white};
  padding: 0;
  display: flex;
  flex-direction: column;

  @media only screen and ${breakpoint.device.m} {
    max-width: 430px;
    height: auto;
    top: auto;
    left: auto;
    border-radius: 4px;
    box-shadow: 0 2px 10px 4px rgba(0, 0, 0, 0.11);
    position: absolute;
    margin-top: 10px;
    max-height: 55vh;
  }
`;

export const StyledFilterDropdownButton = styled.button<StyledFilterDropdownButtonProps>`
  height: 33px;
  ${(props) => (props.$isOpen || props.$count > 0 ? Typography.Subtitle3 : Typography.Body3)}
  background: ${colors.grey25};
  border: 1px solid ${(props) => (props.$count > 0 ? colors.darkBlue100 : colors.grey100)};
  border-radius: 30px;
  padding: 0 30px 0 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  text-transform: capitalize;
  max-width: 116px;

  > span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: ${colors.darkBlue100};
  }

  &::after {
    ${(props) =>
      props.$showCount
        ? css`
            content: '${props.$count}';
            position: absolute;
            right: 10px;
            top: 0;
            bottom: 0;
            margin: auto 0;
            border-radius: 50%;
            color: ${colors.white};
            background: ${colors.darkBlue100};
            height: 15px;
            width: 15px;
            font-size: 9px;
            display: flex;
            align-items: center;
            justify-content: center;
          `
        : css`
            content: '';
            position: absolute;
            right: 13px;
            top: 0;
            bottom: 0;
            margin: auto 0;
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-top: 5px solid ${colors.darkBlue100};
            transition: transform 0.2s ease-in;
            ${props.$isOpen &&
            css`
              transform: rotateZ(180deg);
            `}
          `}
  }

  &:focus {
    outline: none;
  }
`;

export const StyledFilterDropdownHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.grey25};
  padding: 2px 20px 0;
  height: 60px;
  flex-shrink: 0;
  position: sticky;
  z-index: ${zIndex.sticky};
  top: 0;
  width: 100%;
  background: ${colors.white};

  h6 {
    ${Typography.Subtitle2}

    @media only screen and ${breakpoint.device.m} {
      ${Typography.Body1}
    }
  }

  button {
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    width: 42px;
    height: 42px;
    padding-top: 4px;
    cursor: pointer;

    &:focus {
      outline: none;
    }
  }

  @media only screen and ${breakpoint.device.m} {
    justify-content: flex-start;
    position: initial;
    top: auto;
    width: auto;

    > span,
    > button {
      display: none;
    }
  }
`;

export const StyledFilterDropdownBody = styled.div<StyledBodyProps>`
  flex-grow: 1;
  padding: 0 20px;
  border-bottom: 1px solid ${colors.grey25};
  overflow-y: scroll;
  overflow-x: hidden;
  margin-bottom: 85px;

  > * {
    padding: 20px 0;
  }

  > *:not(:last-child) {
    border-bottom: 1px solid ${colors.grey25};
  }

  @media only screen and ${breakpoint.device.m} {
    margin-top: 0;
    margin-bottom: 0;
    border-bottom: none;

    ${scrollbar.primary}

    > *:last-child {
      border-bottom: 1px solid ${colors.grey25};
    }
  }
`;

export const StyledFilterDropdownFooter = styled.footer`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  flex-shrink: 0;
  position: fixed;
  bottom: 0;
  width: 100vw;
  background: ${colors.white};

  > span {
    display: none;
  }

  > button {
    padding: 0 50px;
  }

  @media only screen and ${breakpoint.device.m} {
    flex-direction: row;
    justify-content: space-between;
    position: initial;
    bottom: auto;
    width: auto;

    > span {
      display: block;
    }
  }
`;

export default StyledFilterDropdown;
